import { useState, useEffect, useCallback, useMemo } from 'react';

import type { RovoLogo } from '@atlassian/rovo-connectors-config/icons';
import { SMARTLINKS_RESOURCE_TYPE_PREFIX } from '@atlassian/rovo-connectors-config/schemas/types';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import type { ProductKeys3P as ProductKeys3PType } from '@atlassian/rovo-connectors-config/types';
import { ThirdPartyConfigs } from '@atlassian/rovo-connectors-config/utils';
import {
	get3pSearchConfig,
	update3pConfigCacheEntry,
} from '@atlassian/search-client/fetch-search-config';
import type { T3pResource } from '@atlassian/search-client/fetch-search-config';

import { isValidProductKey } from '../../utils/productKeys';
import {
	resolveRecommendedProductKeys,
	type SmartlinkConnectorRecommendationInputs,
} from '../../utils/smartlink-connector-recommendations';

const DEFAULT_MAX_CONNECTORS = 3;

// The full prefix used in smartlinks connector resource type strings, e.g. 'smartlinks-connector-gdrive'
const SMARTLINKS_CONNECTOR_TYPE_PREFIX = `${SMARTLINKS_RESOURCE_TYPE_PREFIX}-`;

// Dummy/test product keys that should never be shown to users. See https://hello.atlassian.net/wiki/spaces/rdm/pages/6375959349/Knowledge+base#Connectors for details
const DUMMY_SMARTLINKS_PRODUCT_KEYS = new Set(['gdrive-rsl']);

const ATLASSIAN_ID_BASE_URLS = {
	prod: 'https://id.atlassian.com',
	staging: 'https://id.stg.internal.atlassian.com',
} as const;

const getAtlassianIdBaseUrl = (): string => {
	const hostname = window.location.hostname;

	if (hostname.endsWith('.atlassian.net')) {
		return ATLASSIAN_ID_BASE_URLS.prod;
	}

	return ATLASSIAN_ID_BASE_URLS.staging;
};

const buildFallbackAuthUrl = ({
	cloudId,
	serviceKey,
}: {
	cloudId: string;
	serviceKey: string;
}): string =>
	`${getAtlassianIdBaseUrl()}/outboundAuth/start?serviceKey=${serviceKey}&containerId=${cloudId}`;

const normalizeSmartlinksProductKey = (productKey: string): string =>
	productKey === 'gdrive' ? ProductKeys3P.GoogleDrive : productKey;

export interface ConnectorInfo {
	authUrl: string;
	displayName: string;
	icon: RovoLogo | undefined;
	isAuthenticated: boolean;
	productKey: string;
	requiresOauth: boolean;
}

export interface Use3pConnectorResourcesResult {
	connectorResources: ConnectorInfo[];
	isLoading: boolean;
	onConnectorConnected: (productKey: string) => void;
}

/**
 * Builds the fallback connector list shown when the API returns zero valid
 * smartlinks connector resources. Apps come straight from
 * `resolveRecommendedProductKeys`, which guarantees at least Tier 3
 * (HARDCODED_DEFAULTS) is always populated — so no parallel "default app
 * list" is maintained in this file.
 */
const buildFallbackConnectors = async (
	cloudId: string,
	maxConnectors: number,
	recommendedProductKeys: ReadonlyArray<ProductKeys3PType>,
): Promise<ConnectorInfo[]> => {
	return Promise.all(
		recommendedProductKeys.slice(0, maxConnectors).map(async (productKey) => {
			const staticConfig = await ThirdPartyConfigs.get(productKey);

			return {
				productKey,
				displayName: String(staticConfig?.displayName || productKey),
				authUrl: buildFallbackAuthUrl({ cloudId, serviceKey: productKey }),
				icon: staticConfig?.icon,
				requiresOauth: staticConfig?.requiresOauth ?? true,
				isAuthenticated: false,
			};
		}),
	);
};

/**
 * Derives the product key from a smartlinks connector resource type string
 * by stripping the 'smartlinks-connector-' prefix.
 *
 * e.g. 'smartlinks-connector-gdrive' → 'gdrive'
 *      'smartlinks-connector-gdrive-rsl' → 'gdrive-rsl'
 */
const deriveProductKeyFromSmartlinksResourceType = (resourceType: string): string =>
	resourceType.replace(SMARTLINKS_CONNECTOR_TYPE_PREFIX, '');

/**
 * Filters a flat list of T3pResource entries down to valid smartlinks connector
 * resources: those whose type starts with 'smartlinks-connector-', have an
 * outboundAuthUrl, and are not dummy/test products.
 */
const isMatchingConnectorResource = (resource: T3pResource, productKey: string): boolean => {
	if (resource.type === productKey) {
		return true;
	}
	if (resource.type === `${SMARTLINKS_CONNECTOR_TYPE_PREFIX}${productKey}`) {
		return true;
	}
	return resource.type.includes(productKey);
};

const filterSmartlinksConnectorResources = (allResources: T3pResource[]): T3pResource[] =>
	allResources.filter((resource) => {
		if (!resource.type.startsWith(SMARTLINKS_CONNECTOR_TYPE_PREFIX)) {
			return false;
		}
		if (!resource.outboundAuthUrl) {
			return false;
		}
		const derivedProductKey = deriveProductKeyFromSmartlinksResourceType(resource.type);
		if (DUMMY_SMARTLINKS_PRODUCT_KEYS.has(derivedProductKey)) {
			return false;
		}
		return true;
	});

const orderSmartlinksConnectorResources = (
	resources: ReadonlyArray<T3pResource>,
	recommendedProductKeys: ReadonlyArray<ProductKeys3PType>,
	maxConnectors: number,
): T3pResource[] => {
	if (recommendedProductKeys.length === 0) {
		return resources.slice(0, maxConnectors);
	}

	const selectedResources: T3pResource[] = [];
	const selectedIndexes = new Set<number>();

	for (const productKey of recommendedProductKeys) {
		const resourceIndex = resources.findIndex(
			(resource, index) =>
				!selectedIndexes.has(index) && isMatchingConnectorResource(resource, productKey),
		);

		if (resourceIndex >= 0) {
			selectedResources.push(resources[resourceIndex]);
			selectedIndexes.add(resourceIndex);
		}

		if (selectedResources.length >= maxConnectors) {
			return selectedResources;
		}
	}

	for (const [index, resource] of resources.entries()) {
		if (!selectedIndexes.has(index)) {
			selectedResources.push(resource);
		}

		if (selectedResources.length >= maxConnectors) {
			return selectedResources;
		}
	}

	return selectedResources;
};

/**
 * Maps a smartlinks connector T3pResource to a ConnectorInfo object, resolving
 * static display info (icon, displayName, requiresOauth) from ThirdPartyConfigs.
 *
 * The authUrl is taken strictly from resource.outboundAuthUrl — guaranteed to be
 * the Smart Link auth URL, not the full connector auth URL.
 */
const mapSmartlinksResourceToConnectorInfo = async (
	smartlinksResource: T3pResource,
): Promise<ConnectorInfo> => {
	const derivedProductKey = deriveProductKeyFromSmartlinksResourceType(smartlinksResource.type);
	const productKey = normalizeSmartlinksProductKey(derivedProductKey);
	const staticConfig = isValidProductKey(productKey)
		? await ThirdPartyConfigs.get(productKey)
		: undefined;

	return {
		productKey,
		displayName: String(staticConfig?.displayName || productKey),
		authUrl: smartlinksResource.outboundAuthUrl,
		icon: staticConfig?.icon,
		requiresOauth: staticConfig?.requiresOauth ?? true,
		isAuthenticated: smartlinksResource.isUserOAuthed,
	};
};

/**
 * Fetches third-party connector resources from the Smart Link connector API and
 * returns up to `maxConnectors` connectors for display in account setup.
 *
 * IMPORTANT: Only resources whose type starts with 'smartlinks-connector-' are
 * included. This guarantees that the authUrl comes strictly from the Smart Link
 * connector, not a full connector that may override it.
 *
 * If the API returns zero valid smartlinks connector resources, a hardcoded
 * fallback set of apps (Google Drive, OneDrive, GitHub) is returned instead,
 * sliced to `maxConnectors`. On error, the same fallback set is returned.
 *
 * @param cloudId        - The Atlassian cloud ID for the current site.
 * @param userId         - Optional user ID for cache keying.
 * @param maxConnectors  - Maximum number of connectors to return (default 3).
 */
export const use3pConnectorResources = ({
	cloudId,
	userId,
	maxConnectors = DEFAULT_MAX_CONNECTORS,
	smartlinkConnectorRecommendationInputs,
}: {
	cloudId: string;
	maxConnectors?: number;
	smartlinkConnectorRecommendationInputs?: SmartlinkConnectorRecommendationInputs;
	userId?: string;
}): Use3pConnectorResourcesResult => {
	const [connectorResources, setConnectorResources] = useState<ConnectorInfo[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const siteWideSmartlinkAppCounts =
		smartlinkConnectorRecommendationInputs?.siteWideSmartlinkAppCounts;
	const teamTypeSmartlinkAppCounts =
		smartlinkConnectorRecommendationInputs?.teamTypeSmartlinkAppCounts;
	// Full 3-tier cascade so cold-start tenants get the team-type-curated
	// Tier 3 row (or the `na` floor if no team type is known) directly from
	// the canonical recommendation source — no parallel "default app list"
	// is maintained inside the hook.
	const recommendedProductKeys = useMemo(
		() =>
			resolveRecommendedProductKeys(
				{
					siteWideSmartlinkAppCounts,
					teamTypeSmartlinkAppCounts,
					teamTypeKey: smartlinkConnectorRecommendationInputs?.teamTypeKey,
				},
				maxConnectors,
			),
		[
			siteWideSmartlinkAppCounts,
			teamTypeSmartlinkAppCounts,
			smartlinkConnectorRecommendationInputs?.teamTypeKey,
			maxConnectors,
		],
	);

	useEffect(() => {
		setIsLoading(true);

		(async () => {
			try {
				const allResources = await get3pSearchConfig({
					cloudId,
					userId,
					expiration: 1000 * 60,
					returnExpired: true,
					onlyRefreshIfExpired: true,
				});

				const smartlinksConnectorResources = orderSmartlinksConnectorResources(
					filterSmartlinksConnectorResources(allResources),
					recommendedProductKeys,
					maxConnectors,
				);

				if (smartlinksConnectorResources.length === 0) {
					const fallbackConnectors = await buildFallbackConnectors(
						cloudId,
						maxConnectors,
						recommendedProductKeys,
					);
					setConnectorResources(fallbackConnectors);
				} else {
					const resolvedConnectors = await Promise.all(
						smartlinksConnectorResources.map(mapSmartlinksResourceToConnectorInfo),
					);
					setConnectorResources(resolvedConnectors);
				}
			} catch {
				const fallbackConnectors = await buildFallbackConnectors(
					cloudId,
					maxConnectors,
					recommendedProductKeys,
				);
				setConnectorResources(fallbackConnectors);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [cloudId, userId, maxConnectors, recommendedProductKeys]);

	const onConnectorConnected = useCallback(
		(productKey: string) => {
			setConnectorResources((currentResources) =>
				currentResources.map((resource) =>
					resource.productKey === productKey ? { ...resource, isAuthenticated: true } : resource,
				),
			);

			if (userId) {
				void update3pConfigCacheEntry(userId, cloudId, (config) =>
					config.map((resource) =>
						isMatchingConnectorResource(resource, productKey)
							? { ...resource, isUserOAuthed: true }
							: resource,
					),
				);
			}
		},
		[cloudId, userId],
	);

	return { connectorResources, isLoading, onConnectorConnected };
};
